<template>
  <div :class="$style.container">
    <div
      :class="$style.list"
    >
      <div
        :class="[$style.item,
                 $style.loadingItem, {
                   [$style.finishing]: finishedOrSuccess,
                 }]"
      >
        <Checkmark
          v-if="loadingItem === successText"
          :class="$style.checkmark"
        />
        <LoadingIcon
          v-else
          :class="$style.checkmark"
        />
        <span :class="$style.loadingText">{{ loadingItem || featureList[0] }}</span>
      </div>
    </div>
    <div
      :class="[$style.listContainer, {
        [$style.listContainerBackground]: finishedOrSuccess,
      }]"
    >
      <transition name="fade">
        <div
          v-if="finishedOrSuccess"
          :class="$style.listHeader"
        >
          <h3 :class="$style.headerText">
            Getting Started
          </h3>
          <ProgressBar
            :numComplete="totalComplete"
            :numItems="total"
          />
        </div>
      </transition>
      <ul
        :class="$style.list"
      >
        <li
          v-for="item in featureList"
          :key="item"
          data-test-id="featureListItem"
        >
          <transition-group name="bounce">
            <div
              v-if="listMap[item]"
              :key="`${item}0`"
              :class="[$style.borderAndShadow, $style.item]"
            >
              <CheckmarkGray :class="$style.checkmark" />
              <span :class="$style.text">{{ item }}</span>
            </div>
            <div
              v-else
              :key="`${item}1`"
              :class="[$style.borderAndShadow, $style.item]"
            >
              <PlaceHolder :class="$style.placeholder" />
            </div>
          </transition-group>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { timeoutFunction } from 'client/util/timeoutUtils';
import Checkmark from 'public/img/icons/checkmark.svg';
import CheckmarkGray from 'public/img/icons/checkmark-gray.svg';
import LoadingIcon from 'components/LoadingOverlay/LoadingIcon.vue';
import PlaceHolder from 'public/img/icons/onboarding-checklist-placeholder.svg';
import ProgressBar from 'client/components/ImplementationChecklist/components/ProgressBar.vue';

const onboardingModule = 'OnboardingModule';

export default {
  name: 'SimplifiedOnboardingLoadingAnimation',

  components: {
    Checkmark,
    CheckmarkGray,
    LoadingIcon,
    PlaceHolder,
    ProgressBar,
  },

  props: {
    featureList: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      finishedText: 'Finishing',
      listMap: {},
      loadingItem: '',
      logo: Checkmark,
      show: false,
      successText: 'Success',
      transitionName: '',
    };
  },

  computed: {
    ...mapGetters(onboardingModule, {
      total: 'getImplementationChecklistTotal',
      totalComplete: 'getImplementationChecklistTotalComplete',
    }),

    finishedOrSuccess() {
      return this.loadingItem === this.finishedText || this.loadingItem === this.successText;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    animationComplete() {
      timeoutFunction(() => {
        this.$emit('animationComplete');
      }, 1000);
    },

    animationSuccess() {
      this.transitionName = 'background';
      this.loadingItem = this.finishedText;
      timeoutFunction(() => {
        this.loadingItem = this.successText;
        timeoutFunction(() => {
          this.transitionName = 'slide';
          this.animationComplete();
        }, 1500);
      }, 500);
    },

    handleMounted() {
      let counter = 0;
      let renderInterval;
      timeoutFunction(() => {
        renderInterval = setInterval(() => {
          this.loadingItem = this.featureList[counter];
          this.$set(this.listMap, this.featureList[counter], true);
          if (counter > this.featureList.length) {
            clearInterval(renderInterval);
            this.animationSuccess();
          }
          counter += 1;
        }, 500);
      }, 1000);
    },
  },
};
</script>

<style module>
.container {
  --transition-medium: 1s;
  --transition-long: 1.5s;
}

.checkmark {
  flex-shrink: 0;
  height: 1.5rem;
}

.item {
  background: white;
  margin-block-end: 0.5rem;
  padding: 1rem;
  position: relative;
  text-align: center;
  transition: all 0.3s ease-in-out;
  z-index: 1;
}

.loadingItem {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.borderAndShadow {
  align-items: center;
  border: 2px solid var(--jcGrey200);
  border-radius: 0.25rem;
  box-shadow: 0 0 1rem rgba(0, 0, 0, 0.2);
  display: flex;
  text-align: initial;
}

.list {
  list-style: none;
  margin: 20px auto;
  max-width: 40ch;
  padding: 0;
}

.listContainer {
  background-image: linear-gradient(to bottom, transparent 50%, var(--jcGrey200) 50%);
  background-size: 100% 200%;
  border-radius: 0.5rem;
  height: 100vh;
  margin: 0 auto;
  max-width: 45ch;
  transition: background-position var(--transition-long);
  width: 45ch;
}

.listContainerBackground {
  background-position: 0 100%;
}

.listHeader {
  background-color: rgba(146, 153, 158, 0.1);
  border-radius: 0.5rem;
  height: 110px;
  padding: 1rem;
  text-align: center;
  transition: all var(--transition-medium);
}

.finishing {
  text-align: center;
}

.placeholder {
  height: 1rem;
  left: 1rem;
  position: relative;
  transition: all var(--jc-transition-duration) var(--jc-transition-function);
}

.text {
  margin: 0 10px;
}

.loadingText {
  margin: 5px 10px;
}

</style>

<style>
.bounce-enter-active {
  animation: bounce-in 0.4s;
}

.bounce-leave-active {
  display: none;
  opacity: 0;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.3);
  }

  75% {
    transform: scale(1.25);
  }

  90% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}

.fade-enter-active {
  animation: fade-in var(--transition-long);
}

.fade-leave-active {
  opacity: 0;
}

@keyframes fade-in {
  0% {
    height: 0;
    opacity: 0;
  }

  30% {
    height: 0;
    opacity: 0;
  }

  100% {
    height: 110px;
    opacity: 1;
  }
}
</style>
